import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Shield, CheckCircle2, AlertCircle, Banknote, Building2, Mail, Settings2, XCircle, Ban, RotateCw } from 'lucide-react';
import { fmtEUR, fmtDate, STATUS_BADGE, MANDATE_BADGE } from './sepaHelpers';
import SepaPlanExtras from './SepaPlanExtras';
import { Phone } from 'lucide-react';

export default function SepaPlanCard({
  plan, isOpen, toggleExpand, isSuperAdmin, busy,
  handleRetryInstallment, handleCancel, handleRevoke, handleToggleAutoRetry, setCustomizePlan, onChanged,
}) {
  const t = plan._totals || {};
  const mandate = MANDATE_BADGE[plan.mandate_status] || { label: plan.mandate_status, cls: 'bg-slate-200 text-slate-700' };
  const progress = plan.montant_total_cents > 0
    ? Math.round((t.paid_cents || 0) / plan.montant_total_cents * 100) : 0;
  return (
    <div
      className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden transition-all hover:shadow-[0_12px_40px_-12px_rgba(10,61,98,0.18)]"
      data-testid={`sepa-plan-${plan.souscription_id}`}
    >
      {/* Header */}
      <button
        onClick={() => toggleExpand(plan.sepa_plan_id)}
        className="w-full p-5 text-left flex items-center justify-between gap-3 flex-wrap"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-[#0a3d62]/8 flex items-center justify-center shrink-0">
            <Banknote className="h-5 w-5 text-[#0a3d62]" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-[#0a3d62] truncate" data-testid={`sepa-plan-rcd-${plan.souscription_id}`}>RCD #{plan.souscription_id}</p>
            {plan.subscriber?.raison_sociale && (
              <p className="text-sm font-semibold text-slate-700 truncate flex items-center gap-1.5 mt-0.5" data-testid={`sepa-plan-raison-${plan.souscription_id}`}>
                <Building2 className="h-3.5 w-3.5 text-[#0a3d62] shrink-0" />
                {plan.subscriber.raison_sociale}
              </p>
            )}
            <p className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
              <Mail className="h-3 w-3" /> {plan.user_email || '—'}
              {plan.rum && (
                <span className="font-mono text-[10px] text-slate-400" data-testid={`sepa-plan-rum-${plan.souscription_id}`}>{plan.rum}</span>
              )}
              {plan.subscriber?.siret && (
                <span className="inline-flex items-center gap-1 text-slate-500" data-testid={`sepa-plan-siret-${plan.souscription_id}`}>
                  <span className="text-slate-300">·</span>
                  <span className="font-mono">SIRET&nbsp;{plan.subscriber.siret}</span>
                </span>
              )}
              {plan.subscriber?.territoires?.length > 0 && (
                <span className="inline-flex items-center gap-1 text-slate-500" data-testid={`sepa-plan-territoires-${plan.souscription_id}`}>
                  <span className="text-slate-300">·</span>
                  <span>{plan.subscriber.territoires.join(' · ')}</span>
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {plan.resignature_demandee && plan.mandate_status !== 'active' && (
            <Badge className="bg-violet-600 text-white font-semibold" data-testid={`sepa-resignature-badge-${plan.souscription_id}`}>
              Re-signature demandée
            </Badge>
          )}
          {(plan.relances || []).length > 0 && (
            <Badge
              className={`${(plan.relances || []).length >= 3 ? 'bg-orange-500' : 'bg-slate-500'} text-white font-semibold`}
              data-testid={`sepa-relances-badge-${plan.souscription_id}`}
            >
              {(plan.relances || []).length} relance{(plan.relances || []).length > 1 ? 's' : ''}
              {(plan.relances || []).length >= 3 ? ' — suivi téléphonique conseillé' : ''}
            </Badge>
          )}
          {plan.subscriber?.telephone && (
            <Badge className="bg-slate-700 text-white font-semibold" data-testid={`sepa-plan-tel-${plan.souscription_id}`}>
              <Phone className="h-3 w-3 mr-1" />{plan.subscriber.telephone}
            </Badge>
          )}
          <div className="flex items-center gap-1.5 text-xs">
            <Building2 className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-slate-600">Acompte 60%:</span>
            <Badge className="bg-emerald-700 text-white border-emerald-900 font-semibold">Reçu</Badge>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <Badge
              className={plan.rail === 'stripe' ? 'bg-[#635bff] text-white font-semibold' : 'bg-slate-600 text-white font-semibold'}
              data-testid={`sepa-plan-rail-${plan.souscription_id}`}
            >
              {plan.rail === 'stripe' ? 'Stripe' : 'GoCardless'}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <Shield className="h-3.5 w-3.5 text-[#DEA900]" />
            <Badge className={`${mandate.cls} font-semibold`}>{mandate.label}</Badge>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Réglé / Total</div>
            <div className="text-sm font-bold text-[#0a3d62] tabular-nums">
              {fmtEUR(t.paid_cents)} / {fmtEUR(plan.montant_total_cents)}
            </div>
          </div>
        </div>
      </button>

      {/* Progress bar */}
      <div className="px-5 pb-3">
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-700"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-1 text-[10px] tabular-nums">
          <span className="text-emerald-700 font-semibold">{t.paid_count || 0} / {plan.installments?.length || 3} échéances</span>
          <span className="text-[#b88600] font-semibold">Restant : {fmtEUR(t.remaining_cents)}</span>
        </div>
      </div>

      {/* Expand : Tableau des échéances */}
      {isOpen && (
        <div className="border-t border-slate-100 bg-slate-50/50 p-5">
          <p className="text-[11px] uppercase tracking-wider text-[#0a3d62] font-bold mb-3">Détail des échéances</p>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600">#</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600">Date</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600">Montant</th>
                  <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600">Statut</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600">Payment ID</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {(plan.installments || []).map((inst) => {
                  const b = STATUS_BADGE[inst.status] || { label: inst.status, cls: 'bg-slate-100 text-slate-700' };
                  const isFailed = ['failed', 'chargedback', 'creation_failed'].includes(inst.status);
                  return (
                    <tr key={inst.index} className={`border-b border-slate-100 last:border-b-0 ${isFailed ? 'bg-rose-50/40' : ''}`}>
                      <td className="px-3 py-2 font-bold text-[#0a3d62]">{inst.index}{inst.retry_count ? <span className="ml-1 text-[9px] text-amber-700">·R{inst.retry_count}</span> : null}</td>
                      <td className="px-3 py-2 text-slate-700">{fmtDate(inst.charge_date)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtEUR(inst.amount_cents)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded ${b.cls}`}>
                          {inst.status === 'paid' && <CheckCircle2 className="inline h-3 w-3 mr-1" />}
                          {isFailed && <AlertCircle className="inline h-3 w-3 mr-1" />}
                          {b.label}
                        </span>
                        {inst.failure_reason && (
                          <div className="text-[10px] text-rose-700 mt-1 max-w-[180px] mx-auto truncate" title={inst.failure_reason}>
                            {inst.failure_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-500 truncate max-w-[140px]">
                        {inst.payment_id || '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isFailed && isSuperAdmin && plan.mandate_status === 'active' && (
                          <Button
                            size="sm"
                            onClick={() => handleRetryInstallment(plan, inst)}
                            disabled={busy === plan.sepa_plan_id}
                            className="bg-amber-500 hover:bg-amber-600 text-white h-7 px-2 text-[11px]"
                            data-testid={`sepa-retry-${plan.souscription_id}-${inst.index}`}
                          >
                            <RotateCw className="h-3 w-3 mr-1" /> Relancer
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {plan.rail === 'stripe' ? (
            <div className="mt-3 text-[11px] text-slate-500 font-mono">
              Mandat Stripe : {plan.mandate_id || plan.payment_method_id || '—'} · Customer : {plan.customer_id || '—'}
            </div>
          ) : plan.gocardless_mandate_id ? (
            <div className="mt-3 text-[11px] text-slate-500 font-mono">
              Mandat GoCardless : {plan.gocardless_mandate_id} · Customer : {plan.gocardless_customer_id || '—'}
            </div>
          ) : null}

          {/* Extras : relances, note interne, agenda */}
          {isSuperAdmin && <SepaPlanExtras plan={plan} onChanged={onChanged} />}

          {/* Actions admin */}
          {isSuperAdmin && (
            <div className="mt-4 pt-3 border-t border-slate-200 flex flex-wrap gap-2" data-testid={`sepa-admin-actions-${plan.souscription_id}`}>
              <Button
                size="sm"
                onClick={() => setCustomizePlan(plan)}
                disabled={busy === plan.sepa_plan_id || (plan.installments || []).some((i) => i.payment_id)}
                className="bg-[#0a3d62] hover:bg-[#0c4a78] text-white"
                data-testid={`sepa-customize-btn-${plan.souscription_id}`}
                title={(plan.installments || []).some((i) => i.payment_id) ? 'Annulez d\'abord les prélèvements existants' : 'Modifier nb d\'échéances, dates, montants, référence'}
              >
                <Settings2 className="h-4 w-4 mr-1.5" /> Personnaliser l'échéancier
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleCancel(plan)}
                disabled={busy === plan.sepa_plan_id || plan.plan_status === 'cancelled'}
                className="border-amber-400 text-amber-700 hover:bg-amber-50"
                data-testid={`sepa-cancel-btn-${plan.souscription_id}`}
              >
                <Ban className="h-4 w-4 mr-1.5" /> Annuler l'échéancier
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRevoke(plan)}
                disabled={busy === plan.sepa_plan_id || plan.mandate_status === 'revoked' || (plan.rail !== 'stripe' && !plan.gocardless_mandate_id)}
                className="border-rose-400 text-rose-700 hover:bg-rose-50"
                data-testid={`sepa-revoke-btn-${plan.souscription_id}`}
              >
                <XCircle className="h-4 w-4 mr-1.5" /> Révoquer le mandat
              </Button>
              {/* Toggle auto-retry */}
              <Button
                onClick={() => handleToggleAutoRetry(plan)}
                disabled={busy === plan.sepa_plan_id}
                variant="outline"
                className={`${plan.auto_retry_disabled
                  ? 'border-amber-400 text-amber-700 hover:bg-amber-50'
                  : 'border-emerald-400 text-emerald-700 hover:bg-emerald-50'}`}
                data-testid={`sepa-auto-retry-toggle-${plan.souscription_id}`}
                title={plan.auto_retry_disabled
                  ? "L'auto-relance est désactivée pour ce plan. Cliquez pour la réactiver."
                  : "L'auto-relance est active (max 2 tentatives, cooldown 3 j ouvrés). Cliquez pour la désactiver."}
              >
                <RotateCw className={`h-4 w-4 mr-1.5 ${plan.auto_retry_disabled ? '' : 'text-emerald-600'}`} />
                {plan.auto_retry_disabled ? 'Auto-relance OFF' : 'Auto-relance ON'}
              </Button>
              {busy === plan.sepa_plan_id && <Loader2 className="h-4 w-4 animate-spin text-[#0a3d62] self-center" />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
